import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Slider,
    Typography,
    IconButton,
    Tooltip,
} from "@mui/material";
import AvatarEditor from "react-avatar-editor";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIcon from "@mui/icons-material/Close";
import CropIcon from "@mui/icons-material/Crop";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import "./ImageCropModal.css";

// ─────────────────────────────────────────────────────────────────────────────
// Shared helper: computes AvatarEditor preview dimensions responsively
// ─────────────────────────────────────────────────────────────────────────────
const computePreviewSize = (outputWidth, outputHeight, maxW = 440, maxH = 300) => {
    const vw = window.innerWidth;
    // Shrink caps on smaller screens
    const effectiveMaxW = vw <= 480 ? Math.min(maxW, vw - 40) : vw <= 768 ? Math.min(maxW, vw - 160) : maxW;
    const effectiveMaxH = vw <= 480 ? Math.min(maxH, 220) : vw <= 768 ? Math.min(maxH, 260) : maxH;

    const ratio = outputWidth / outputHeight;
    let previewW, previewH;
    if (ratio >= 1) {
        previewW = Math.min(outputWidth, effectiveMaxW);
        previewH = Math.round(previewW / ratio);
        if (previewH > effectiveMaxH) { previewH = effectiveMaxH; previewW = Math.round(previewH * ratio); }
    } else {
        previewH = Math.min(outputHeight, effectiveMaxH);
        previewW = Math.round(previewH * ratio);
        if (previewW > effectiveMaxW) { previewW = effectiveMaxW; previewH = Math.round(previewW / ratio); }
    }
    if (previewW < 100) { const s = 100 / previewW; previewW = 100; previewH = Math.round(previewH * s); }
    if (previewH < 70) { const s = 70 / previewH; previewH = 70; previewW = Math.round(previewW * s); }
    return { previewW, previewH };
};

// ─────────────────────────────────────────────────────────────────────────────
// CropEditor — shared inner editor panel (used in both modes)
// ─────────────────────────────────────────────────────────────────────────────
const CropEditor = ({
    imageSrc,
    previewW,
    previewH,
    borderRadius,
    scale,
    rotate,
    setScale,
    setRotate,
    editorRef,
    outputWidth,
    outputHeight,
}) => (
    <div className="icm-crop-editor">
        {imageSrc && (
            <div className="icm-avatar-wrapper">
                <AvatarEditor
                    ref={editorRef}
                    image={imageSrc}
                    width={previewW}
                    height={previewH}
                    border={20}
                    borderRadius={borderRadius}
                    color={[0, 0, 0, 0.5]}
                    scale={scale}
                    rotate={rotate}
                    onLoadSuccess={(imgInfo) => {
                        if (imgInfo?.width && imgInfo?.height) {
                            const imgRatio = imgInfo.width / imgInfo.height;
                            const cropRatio = previewW / previewH;
                            let initialScale = 1;
                            if (imgRatio > cropRatio) { initialScale = cropRatio / imgRatio; }
                            else if (imgRatio < cropRatio) { initialScale = imgRatio / cropRatio; }
                            setScale(Math.max(0.1, initialScale));
                        }
                    }}
                    className="icm-avatar-canvas"
                />
            </div>
        )}
        <Typography variant="caption" className="icm-hint-text">
            Drag to reposition · Scroll or slide to zoom
        </Typography>
        <Typography variant="caption" className="icm-size-text">
            Output size: {outputWidth} × {outputHeight} px
        </Typography>
        <div className="icm-slider-block">
            <Typography variant="body2" className="icm-slider-label">Zoom</Typography>
            <Slider value={scale} min={0.1} max={3} step={0.01} onChange={(_, v) => setScale(v)} sx={{ color: "var(--all-menus-color, #611f1e)" }} />
        </div>
        <div className="icm-slider-block">
            <Typography variant="body2" className="icm-slider-label">Rotate</Typography>
            <Slider value={rotate} min={-180} max={180} step={1} onChange={(_, v) => setRotate(v)} sx={{ color: "var(--all-menus-color, #611f1e)" }} />
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ModalHeader — shared gradient header used by both single & multi modes
// ─────────────────────────────────────────────────────────────────────────────
const ModalHeader = ({ title, onCancel, badge }) => (
    <DialogTitle className="icm-header">
        <span className="icm-header-left">
            <CropIcon className="icm-header-icon" />
            {title}
        </span>
        <div className="icm-header-right">
            {badge}
            <IconButton size="small" onClick={onCancel} className="icm-close-btn">
                <CloseIcon fontSize="small" />
            </IconButton>
        </div>
    </DialogTitle>
);

// ─────────────────────────────────────────────────────────────────────────────
// MultiImageCropModal — split-panel multi-image mode
// ─────────────────────────────────────────────────────────────────────────────
const MultiImageCropModal = ({ open, images, onCropAll, onCancel, onRemoveImage, outputWidth, outputHeight, title }) => {
    const editorRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    // Map of index -> cropped base64
    const [croppedMap, setCroppedMap] = useState({});

    const { previewW, previewH } = computePreviewSize(outputWidth, outputHeight, 420, 300);
    const ratio = outputWidth / outputHeight;
    const isSquare = Math.abs(ratio - 1) < 0.02;
    const borderRadius = isSquare ? previewW / 2 : 8;

    // Reset when modal opens or images change
    useEffect(() => {
        if (open) {
            setActiveIndex(0);
            setScale(1);
            setRotate(0);
            setCroppedMap({});
        }
    }, [open, images]);

    // Reset zoom/rotate when switching images
    useEffect(() => {
        setScale(1);
        setRotate(0);
    }, [activeIndex]);

    const cropCount = Object.keys(croppedMap).length;
    const allCropped = images?.length > 0 && cropCount === images.length;

    const handleCropThisImage = useCallback(() => {
        if (!editorRef.current) return;
        const canvas = editorRef.current.getImage();
        const out = document.createElement("canvas");
        out.width = outputWidth;
        out.height = outputHeight;
        const ctx = out.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(canvas, 0, 0, outputWidth, outputHeight);
        const base64 = out.toDataURL("image/png");

        const updatedMap = { ...croppedMap, [activeIndex]: base64 };
        setCroppedMap(updatedMap);

        // Auto-advance to next uncropped image
        const anyUncropped = images.findIndex((_, i) => !updatedMap[i]);
        if (anyUncropped !== -1 && anyUncropped !== activeIndex) {
            setActiveIndex(anyUncropped);
        }
    }, [activeIndex, croppedMap, images, outputWidth, outputHeight]);

    const handleConfirmAll = () => {
        if (!allCropped) return;
        const result = images.map((_, i) => croppedMap[i]);
        onCropAll(result);
    };

    if (!open || !images?.length) return null;

    const progressBadge = (
        <span className="icm-progress-badge">
            <CheckCircleIcon className={`icm-progress-icon ${cropCount === images.length ? "icm-progress-icon--done" : "icm-progress-icon--pending"}`} />
            {cropCount} / {images.length} cropped
        </span>
    );

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth PaperProps={{ className: "icm-paper" }}>
            <ModalHeader title={title || "Crop Images"} onCancel={onCancel} badge={progressBadge} />

            <DialogContent className="icm-body">
                {/* ── Left: Thumbnail Strip ── */}
                <div className="icm-strip">
                    <Typography variant="caption" className="icm-strip-label">
                        Images
                    </Typography>
                    {images.map((src, i) => {
                        const isCropped = !!croppedMap[i];
                        const isActive = activeIndex === i;
                        return (
                            <Tooltip key={i} title={isCropped ? `Image ${i + 1} — Cropped ✅` : `Image ${i + 1} — Click to crop`} placement="right">
                                <div onClick={() => setActiveIndex(i)} className={`icm-thumb ${isActive ? "icm-thumb--active" : isCropped ? "icm-thumb--cropped" : "icm-thumb--pending"}`}>
                                    <img src={isCropped ? croppedMap[i] : src} alt={`img-${i + 1}`} className={`icm-thumb-img ${!isActive ? "icm-thumb-img--inactive" : ""}`} />
                                    {/* Status badge */}
                                    <div className="icm-thumb-badge">
                                        {isCropped ? <CheckCircleIcon className="icm-thumb-badge-icon--done" /> : <RadioButtonUncheckedIcon className="icm-thumb-badge-icon--pending" />}
                                    </div>
                                    {/* Index label */}
                                    <div className="icm-thumb-index">{i + 1}</div>
                                    {/* Remove button — visible on hover */}
                                    {typeof onRemoveImage === 'function' && (
                                        <button
                                            className="icm-thumb-remove"
                                            title={`Remove image ${i + 1}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onRemoveImage(i);
                                            }}
                                        >
                                            <DeleteForeverIcon className="icm-thumb-remove-icon" />
                                        </button>
                                    )}
                                </div>
                            </Tooltip>
                        );
                    })}
                </div>

                {/* ── Right: Crop Editor Panel ── */}
                <div className="icm-editor-panel">
                    {/* Active image counter */}
                    <div className="icm-image-counter-row">
                        <Typography variant="subtitle2" className="icm-image-counter-label">
                            Image {activeIndex + 1} of {images.length}
                        </Typography>
                        {croppedMap[activeIndex] && (
                            <span className="icm-cropped-badge">
                                <CheckCircleIcon className="icm-cropped-badge-icon" />
                                Cropped
                            </span>
                        )}
                    </div>

                    {/* ── Canvas row: Prev | Editor + Preview | Next ── */}
                    <div className="icm-canvas-row">

                        {/* ← Prev arrow */}
                        <Button
                            variant="outlined"
                            disabled={activeIndex === 0}
                            onClick={() => setActiveIndex(i => i - 1)}
                            className="icm-btn-canvas-nav"
                        >
                            &#8592;
                        </Button>

                        {/* Centre column: canvas + preview */}
                        <div className="icm-canvas-center">
                            {/* AvatarEditor */}
                            {images[activeIndex] && (
                                <div className="icm-avatar-wrapper">
                                    <AvatarEditor
                                        ref={editorRef}
                                        image={images[activeIndex]}
                                        width={previewW}
                                        height={previewH}
                                        border={20}
                                        borderRadius={borderRadius}
                                        color={[0, 0, 0, 0.5]}
                                        scale={scale}
                                        rotate={rotate}
                                        onLoadSuccess={(imgInfo) => {
                                            if (imgInfo?.width && imgInfo?.height) {
                                                const imgRatio = imgInfo.width / imgInfo.height;
                                                const cropRatio = previewW / previewH;
                                                let initialScale = 1;
                                                if (imgRatio > cropRatio) { initialScale = cropRatio / imgRatio; }
                                                else if (imgRatio < cropRatio) { initialScale = imgRatio / cropRatio; }
                                                setScale(Math.max(0.1, initialScale));
                                            }
                                        }}
                                        className="icm-avatar-canvas"
                                    />
                                </div>
                            )}

                            {/* Cropped preview directly below canvas */}
                            {/* <div className="icm-preview-box">
                                <Typography variant="caption" className="icm-preview-label">
                                    {croppedMap[activeIndex] ? "Cropped Preview" : "Preview"}
                                </Typography>
                                {croppedMap[activeIndex] ? (
                                    <img
                                        src={croppedMap[activeIndex]}
                                        alt={`preview-${activeIndex + 1}`}
                                        className="icm-preview-img"
                                    />
                                ) : (
                                    <div className="icm-preview-placeholder">
                                        <CropFreeIcon className="icm-preview-placeholder-icon" />
                                        <span>Apply crop to see preview</span>
                                    </div>
                                )}
                            </div> */}
                        </div>

                        {/* Next → arrow */}
                        <Button
                            variant="outlined"
                            disabled={activeIndex === images.length - 1}
                            onClick={() => setActiveIndex(i => i + 1)}
                            className="icm-btn-canvas-nav"
                        >
                            &#8594;
                        </Button>
                    </div>

                    {/* Hint + size text */}
                    <Typography variant="caption" className="icm-hint-text">
                        Drag to reposition · Scroll or slide to zoom
                    </Typography>
                    <Typography variant="caption" className="icm-size-text">
                        Output size: {outputWidth} × {outputHeight} px
                    </Typography>

                    {/* Zoom slider */}
                    <div className="icm-slider-block">
                        <Typography variant="body2" className="icm-slider-label">Zoom</Typography>
                        <Slider value={scale} min={0.1} max={3} step={0.01} onChange={(_, v) => setScale(v)} sx={{ color: "var(--all-menus-color, #611f1e)" }} />
                    </div>

                    {/* Rotate slider */}
                    <div className="icm-slider-block">
                        <Typography variant="body2" className="icm-slider-label">Rotate</Typography>
                        <Slider value={rotate} min={-180} max={180} step={1} onChange={(_, v) => setRotate(v)} sx={{ color: "var(--all-menus-color, #611f1e)" }} />
                    </div>

                    {/* Apply Crop button */}
                    <Button
                        onClick={handleCropThisImage}
                        variant="contained"
                        startIcon={<CropIcon />}
                        className={`icm-btn-crop-image ${croppedMap[activeIndex] ? "icm-btn-crop-image--recrop" : "icm-btn-crop-image--default"}`}
                    >
                        {croppedMap[activeIndex] ? "Adjust Crop" : "Apply Crop"}
                    </Button>
                </div>
            </DialogContent>

            {/* ── Footer ── */}
            <DialogActions className="icm-footer">
                <Button onClick={onCancel} variant="outlined" className="icm-btn-cancel">
                    Cancel
                </Button>
                <div className="icm-footer-right">
                    {!allCropped && (
                        <Typography variant="caption" className="icm-footer-hint">
                            Crop all {images.length} images to confirm
                        </Typography>
                    )}
                    <Tooltip title={!allCropped ? `${images.length - cropCount} image(s) still need cropping` : "All images cropped — ready to confirm!"}>
                        <span>
                            <Button onClick={handleConfirmAll} disabled={!allCropped} variant="contained" startIcon={<DoneAllIcon />} className={`icm-btn-confirm ${allCropped ? "icm-btn-confirm--ready" : ""}`}>
                                Confirm All ({cropCount}/{images.length})
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </DialogActions>
        </Dialog>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ImageCropModal — main export (routes to single or multi mode)
// ─────────────────────────────────────────────────────────────────────────────
const ImageCropModal = ({
    open,
    imageSrc,
    onCancel,
    onCrop,
    outputWidth = 140,
    outputHeight = 140,
    isSquareCrop = false,
    backgroundColor = "transparent",
    title = "Crop Image",
    // Multi-image props
    images,
    onCropAll,
    onRemoveImage,
}) => {
    if (images && images.length > 0 && typeof onCropAll === "function") {
        return (
            <MultiImageCropModal open={open} images={images} onCropAll={onCropAll} onCancel={onCancel} onRemoveImage={onRemoveImage} outputWidth={outputWidth} outputHeight={outputHeight}
                title={title}
            />
        );
    }

    return (
        <SingleImageCropModal open={open} imageSrc={imageSrc} onCancel={onCancel} onCrop={onCrop} outputWidth={outputWidth} outputHeight={outputHeight} isSquareCrop={isSquareCrop} backgroundColor={backgroundColor} title={title}
        />
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SingleImageCropModal — single-image mode with matching header/footer
// ─────────────────────────────────────────────────────────────────────────────
const SingleImageCropModal = ({
    open,
    imageSrc,
    onCancel,
    onCrop,
    outputWidth,
    outputHeight,
    isSquareCrop,
    title,
}) => {
    const editorRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);

    useEffect(() => {
        if (open) { setScale(1); setRotate(0); }
    }, [open, imageSrc]);

    const { previewW, previewH } = computePreviewSize(outputWidth, outputHeight, 480, 340);
    const ratio = outputWidth / outputHeight;
    const isSquare = Math.abs(ratio - 1) < 0.02;
    const borderRadius = (isSquare && !isSquareCrop) ? previewW / 2 : 8;

    const handleCrop = () => {
        if (!editorRef.current) return;
        const canvas = editorRef.current.getImage();
        const out = document.createElement("canvas");
        out.width = outputWidth;
        out.height = outputHeight;
        const ctx = out.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(canvas, 0, 0, outputWidth, outputHeight);
        onCrop(out.toDataURL("image/png"));
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth PaperProps={{ className: "icm-paper" }}>
            {/* Matches multi-image header style */}
            <ModalHeader title={title || "Crop Image"} onCancel={onCancel} />

            <DialogContent className="icm-body--single">
                <CropEditor imageSrc={imageSrc} previewW={previewW} previewH={previewH} borderRadius={borderRadius} scale={scale} rotate={rotate}
                    setScale={setScale} setRotate={setRotate} editorRef={editorRef} outputWidth={outputWidth} outputHeight={outputHeight} />
            </DialogContent>

            {/* Matches multi-image footer style */}
            <DialogActions className="icm-footer icm-footer--single">
                <Button onClick={onCancel} variant="outlined" className="icm-btn-cancel">
                    Cancel
                </Button>
                <Button onClick={handleCrop} variant="contained" startIcon={<CropIcon />} className="icm-btn-apply menus-color">
                    Apply Crop
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImageCropModal;

// ─── useImageCrop hook ───────────────────────────────────────────────────────
export const useImageCrop = () => {
    const [cropModal, setCropModal] = useState({ open: false, index: null, src: null });

    const openCropModal = (event, index = null, maxMB = 5) => {
        const file = event.target.files[0];
        event.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            import("react-toastify").then(({ toast }) => toast.error("Please select an image file."));
            return;
        }
        if (file.size > maxMB * 1024 * 1024) {
            import("react-toastify").then(({ toast }) => toast.error("Image size must be under " + maxMB + "MB."));
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => setCropModal({ open: true, index, src: e.target.result });
        reader.readAsDataURL(file);
    };

    const closeCropModal = () => setCropModal({ open: false, index: null, src: null });

    const handleCropApply = (base64, onSave) => {
        const { index } = cropModal;
        onSave(base64, index);
        closeCropModal();
    };

    return { cropModal, openCropModal, handleCropApply, closeCropModal };
};